import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { CheckCircle2, TrendingUp, Target, Users, ArrowRight } from 'lucide-react';
import LandingNav from '../components/landing/LandingNav';
import FooterSection from '../components/landing/FooterSection';

const VendorEarlyAccessLanding = () => {
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    companyName: '',
    name: '',
    email: '',
    specialty: '',
    location: '',
    linkedinUrl: '',
  });
  const [utmParams, setUtmParams] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [formStarted, setFormStarted] = useState(false);

  // Extract UTM parameters on mount
  useEffect(() => {
    const params = {};
    ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'].forEach(key => {
      const value = searchParams.get(key);
      if (value) params[key] = value;
    });
    setUtmParams(params);
  }, [searchParams]);

  const handleFormStart = () => {
    if (!formStarted && window.posthog) {
      window.posthog.capture('early_access_form_started', {
        form_type: 'vendor_beta',
        utm_source: searchParams.get('utm_source') || undefined,
        utm_campaign: searchParams.get('utm_campaign') || undefined,
        utm_medium: searchParams.get('utm_medium') || undefined,
      });
      setFormStarted(true);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const payload = {
        ...formData,
        type: 'vendor',
        ...utmParams,
      };

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/early-access`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || t('vendor.form.errorGeneric'));
      }

      // Track successful form submission
      if (window.posthog) {
        window.posthog.capture('early_access_form_submitted', {
          form_type: 'vendor_beta',
          utm_source: searchParams.get('utm_source') || undefined,
          utm_campaign: searchParams.get('utm_campaign') || undefined,
          utm_medium: searchParams.get('utm_medium') || undefined,
        });
      }

      setSubmitted(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const SPECIALTIES = [
    { value: 'ai_ml', label: t('vendor.form.specialtyAI') },
    { value: 'custom_dev', label: t('vendor.form.specialtyDev') },
    { value: 'saas', label: t('vendor.form.specialtySaaS') },
    { value: 'digital_strategy', label: t('vendor.form.specialtyStrategy') },
    { value: 'other', label: t('vendor.form.specialtyOther') },
  ];

  const LOCATIONS = [
    { value: 'canada', label: t('vendor.form.locationCanada') },
    { value: 'us', label: t('vendor.form.locationUS') },
  ];

  const BENEFITS = [
    { icon: Target, key: 'preQualified' },
    { icon: TrendingUp, key: 'specialtyMatch' },
    { icon: Users, key: 'inbound' },
    { icon: CheckCircle2, key: 'freeEarly' },
  ];

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
        <Helmet>
          <title>{t('vendor.meta.titleSuccess')}</title>
          <meta name="robots" content="noindex" />
        </Helmet>
        <LandingNav />
        <div className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mx-auto text-center">
            <div className="mb-6">
              <CheckCircle2 className="w-16 h-16 text-primary mx-auto" />
            </div>
            <h1 className="text-4xl font-bold text-slate-900 mb-4">
              {t('vendor.success.title')}
            </h1>
            <p className="text-xl text-slate-600 mb-8">
              {t('vendor.success.description')}
            </p>
            <div className="bg-secondary/30 border border-primary/20 rounded-lg p-6">
              <p className="text-slate-700">
                {t('vendor.success.nextSteps')}
              </p>
            </div>
          </div>
        </div>
        <FooterSection />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <Helmet>
        <title>{t('vendor.meta.title')}</title>
        <meta name="description" content={t('vendor.meta.description')} />
        <meta name="robots" content="noindex" />
      </Helmet>

      <LandingNav />

      {/* Hero Section */}
      <section className="pt-32 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center space-x-2 bg-secondary text-primary px-4 py-2 rounded-full text-sm font-medium mb-6">
            <TrendingUp className="w-4 h-4" />
            <span>{t('vendor.hero.badge')}</span>
          </div>

          <h1 className="text-5xl lg:text-6xl font-bold text-slate-900 leading-tight mb-6">
            {t('vendor.hero.title')}
            <span className="block mt-2 text-primary">
              {t('vendor.hero.titleAccent')}
            </span>
          </h1>

          <p className="text-xl text-slate-600 leading-relaxed mb-8 max-w-3xl mx-auto">
            {t('vendor.hero.description')}
          </p>

          {/* Trust Signal */}
          <div className="inline-flex items-center space-x-2 bg-primary/10 border border-primary/20 px-6 py-3 rounded-lg">
            <CheckCircle2 className="w-5 h-5 text-primary" />
            <p className="text-sm font-medium text-slate-700">
              {t('vendor.hero.trustSignal')}
            </p>
          </div>
        </div>
      </section>

      {/* Benefits Grid */}
      <section className="pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="grid md:grid-cols-2 gap-6">
            {BENEFITS.map(({ icon: Icon, key }) => (
              <div
                key={key}
                className="bg-white border border-slate-200 rounded-lg p-6 hover:border-primary/30 transition-colors"
              >
                <Icon className="w-8 h-8 text-primary mb-4" />
                <h3 className="text-lg font-semibold text-slate-900 mb-2">
                  {t(`vendor.benefits.${key}.title`)}
                </h3>
                <p className="text-slate-600">
                  {t(`vendor.benefits.${key}.description`)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section className="pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mx-auto">
          <div className="bg-white border border-slate-200 rounded-2xl shadow-lg p-8">
            <h2 className="text-3xl font-bold text-slate-900 mb-2 text-center">
              {t('vendor.form.title')}
            </h2>
            <p className="text-slate-600 mb-8 text-center">
              {t('vendor.form.subtitle')}
            </p>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Company Name */}
              <div>
                <Label htmlFor="company_name">{t('vendor.form.companyName')}</Label>
                <Input
                  id="companyName"
                  name="companyName"
                  type="text"
                  required
                  value={formData.companyName}
                  onChange={handleChange}
                  onFocus={handleFormStart}
                  placeholder={t('vendor.form.companyNamePlaceholder')}
                  className="mt-1"
                />
              </div>

              {/* Contact Name */}
              <div>
                <Label htmlFor="contact_name">{t('vendor.form.contactName')}</Label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  placeholder={t('vendor.form.contactNamePlaceholder')}
                  className="mt-1"
                />
              </div>

              {/* Email */}
              <div>
                <Label htmlFor="email">{t('vendor.form.email')}</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  placeholder={t('vendor.form.emailPlaceholder')}
                  className="mt-1"
                />
              </div>

              {/* Specialty */}
              <div>
                <Label htmlFor="specialty">{t('vendor.form.specialty')}</Label>
                <select
                  id="specialty"
                  name="specialty"
                  required
                  value={formData.specialty}
                  onChange={handleChange}
                  className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                >
                  <option value="">{t('vendor.form.specialtyPlaceholder')}</option>
                  {SPECIALTIES.map(({ value, label }) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </div>

              {/* Location */}
              <div>
                <Label htmlFor="location">{t('vendor.form.location')}</Label>
                <select
                  id="location"
                  name="location"
                  required
                  value={formData.location}
                  onChange={handleChange}
                  className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                >
                  <option value="">{t('vendor.form.locationPlaceholder')}</option>
                  {LOCATIONS.map(({ value, label }) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </div>

              {/* LinkedIn URL (optional) */}
              <div>
                <Label htmlFor="linkedin_url">
                  {t('vendor.form.linkedinUrl')}
                  <span className="text-slate-500 text-sm ml-2">({t('vendor.form.optional')})</span>
                </Label>
                <Input
                  id="linkedinUrl"
                  name="linkedinUrl"
                  type="url"
                  value={formData.linkedinUrl}
                  onChange={handleChange}
                  placeholder={t('vendor.form.linkedinUrlPlaceholder')}
                  className="mt-1"
                />
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg text-sm">
                  {error}
                </div>
              )}

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-primary hover:bg-primary/90 text-primary-foreground text-lg py-6 group"
              >
                {loading ? t('vendor.form.submitting') : t('vendor.form.cta')}
                {!loading && <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />}
              </Button>

              <p className="text-xs text-slate-500 text-center">
                {t('vendor.form.disclaimer')}
              </p>
            </form>
          </div>
        </div>
      </section>

      <FooterSection />
    </div>
  );
};

export default VendorEarlyAccessLanding;
